import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { ToastContext } from '@/lib/ToastContext'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

const iconMap: Record<ToastType, string> = {
  success: '\u2713',
  error: '\u2717',
  warning: '\u26A0',
  info: '\u2139',
}

const colorMap: Record<ToastType, string> = {
  success: 'border-l-emerald-500 bg-emerald-500/10',
  error: 'border-l-red-500 bg-red-500/10',
  warning: 'border-l-amber-500 bg-amber-500/10',
  info: 'border-l-blue-500 bg-blue-500/10',
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration ?? 4000)
    return () => clearTimeout(timer)
  }, [toast, onDismiss])

  return (
    <div
      role="alert"
      className={`flex items-center gap-3 px-4 py-3 border-l-4 rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] backdrop-blur-sm ${colorMap[toast.type]}`}
    >
      <span className="text-lg">{iconMap[toast.type]}</span>
      <p className="text-sm text-[var(--color-text-primary)] flex-1">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] text-lg leading-none"
        aria-label="Dismiss"
      >
        &times;
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback((type: ToastType, message: string, duration?: number) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    setToasts((prev) => [...prev, { id, type, message, duration }])
  }, [])

  return (
    <ToastContext value={{ toast: addToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-auto">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext>
  )
}
